import {
  DeesElement,
  customElement,
  property,
  state,
  html,
  css,
} from '@design.estate/dees-element';
import type {
  IKanbanColumn,
  IKanbanComment,
  IKanbanPerson,
  IKanbanProvider,
  IKanbanTicket,
  IKanbanTicketDraft,
  TKanbanPriority,
} from '../interfaces.js';
import type {
  TReferenceContent,
  TReferenceSearch,
} from '../../00group-input/dees-input-reference/interfaces.js';
import { kanbanStyles } from '../kanban.styles.js';
import {
  kanbanEvent,
  kanbanInitials,
  kanbanPriorityIcon,
  sortKanbanTickets,
  ticketDraft,
  ticketLabel,
} from '../kanban.helpers.js';
import type { DeesInputText } from '../../00group-input/dees-input-text/dees-input-text.js';
import type { DeesInputTextarea } from '../../00group-input/dees-input-textarea/dees-input-textarea.js';
import '../../00group-input/dees-input-text/dees-input-text.js';
import '../../00group-input/dees-input-textarea/dees-input-textarea.js';
import '../../00group-input/dees-input-dropdown/dees-input-dropdown.js';
import '../dees-kanban-discussion/dees-kanban-discussion.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-kanban-ticket-detail': DeesKanbanTicketDetail;
  }
}

@customElement('dees-kanban-ticket-detail')
export class DeesKanbanTicketDetail extends DeesElement {
  @property({ attribute: false })
  accessor ticket: IKanbanTicket | undefined;

  @property({ attribute: false })
  accessor tickets: IKanbanTicket[] = [];

  @property({ attribute: false })
  accessor columns: IKanbanColumn[] = [];

  @property({ attribute: false })
  accessor people: IKanbanPerson[] = [];

  @property({ attribute: false })
  accessor provider: IKanbanProvider = {};

  @property({ attribute: false })
  accessor draft: IKanbanTicketDraft | undefined;

  @property({ attribute: false })
  accessor commentDraft: TReferenceContent = [];

  @property({ attribute: false })
  accessor comments: IKanbanComment[] = [];

  @property({ attribute: false })
  accessor searchTickets: TReferenceSearch | undefined;

  @property({ type: Boolean })
  accessor loadingComments = false;

  @property({ type: String })
  accessor commentsError = '';

  @property({ type: String })
  accessor error = '';

  @property({ type: Boolean })
  accessor saving = false;

  @property({ type: Boolean })
  accessor canGoBack = false;

  /** Keep the identifier visible unless the containing surface already labels the ticket. */
  @property({ type: Boolean })
  accessor showTicketKey = true;

  @state()
  private accessor editing = false;

  public static styles = [
    ...kanbanStyles,
    css`
      :host {
        padding: 24px;
      }

      .layout {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 248px;
        grid-template-areas: 'lead props' 'body props';
        gap: 24px;
        align-items: start;
      }

      .lead {
        grid-area: lead;
        min-width: 0;
      }

      .body {
        grid-area: body;
        min-width: 0;
      }

      .toolbar {
        margin-bottom: 20px;
        flex-wrap: nowrap;
        min-width: 0;
      }

      .toolbar .badge {
        text-transform: capitalize;
        flex-shrink: 0;
      }

      .ticket-key {
        flex-shrink: 0;
        font-variant-numeric: tabular-nums;
      }

      .link.parent {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: var(--dees-font-footnote-size);
      }

      .toolbar dees-button {
        flex-shrink: 0;
      }

      h2 {
        font-size: var(--dees-font-title2-size);
        line-height: 1.3;
        font-weight: 600;
        margin-bottom: 12px;
        letter-spacing: -.015em;
        overflow-wrap: anywhere;
      }

      .description {
        white-space: pre-wrap;
        font-size: var(--dees-font-subhead-size);
        line-height: 22px;
        overflow-wrap: anywhere;
        max-width: 64ch;
      }

      aside {
        grid-area: props;
        min-width: 0;
        padding: 14px 16px;
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-settings-group);
      }

      .fields {
        display: grid;
        gap: 14px;
        font-size: var(--dees-font-footnote-size);
        font-weight: 500;
      }

      .fields label {
        display: block;
        margin-bottom: 5px;
        font-size: var(--dees-font-caption1-size);
        font-weight: 400;
      }

      .value {
        min-height: var(--dees-control-height-lg);
        display: flex;
        align-items: center;
      }

      .person {
        display: flex;
        align-items: center;
        gap: 7px;
      }

      .children {
        margin-bottom: 24px;
      }

      .child-list {
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-settings-group);
        overflow: hidden;
      }

      .child {
        display: flex;
        width: 100%;
        align-items: center;
        gap: 10px;
        min-height: 44px;
        padding: 10px 12px;
        border: 0;
        background: transparent;
        color: inherit;
        font: inherit;
        text-align: left;
        cursor: pointer;
      }

      .child + .child {
        border-top: 1px solid var(--dees-color-border-subtle);
      }

      .child:hover {
        background: var(--dees-color-row-hover);
      }

      .child:focus-visible {
        outline-offset: -3px;
      }

      .child-key {
        font-size: var(--dees-font-caption1-size);
        color: var(--dees-color-text-secondary);
        font-variant-numeric: tabular-nums;
        flex-shrink: 0;
      }

      .child-title {
        flex: 1;
        min-width: 0;
        font-size: var(--dees-font-footnote-size);
        font-weight: 500;
        overflow-wrap: anywhere;
      }

      .child .badge {
        flex-shrink: 0;
      }

      .discussion {
        border-top: 1px solid var(--dees-color-border-subtle);
        padding-top: 20px;
      }

      .edit {
        display: grid;
        gap: 14px;
      }

      @media (max-width: 700px) {
        :host {
          padding: 16px;
        }
        .layout {
          grid-template-columns: minmax(0, 1fr);
          grid-template-areas: 'lead' 'props' 'body';
          gap: 20px;
        }
        .fields {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        .toolbar {
          gap: 6px;
          margin-bottom: 16px;
        }
        .child {
          gap: 7px;
        }
      }
    `,
  ];

  public updated(changed: Map<string, unknown>): void {
    const previous = changed.get('ticket') as IKanbanTicket | undefined;
    if (changed.has('ticket') && previous?.id !== this.ticket?.id)
      this.editing = Boolean(this.draft);
  }

  private changeDraft(change: Partial<IKanbanTicketDraft>): void {
    this.draft = { ...(this.draft ?? ticketDraft(this.ticket!)), ...change };
    kanbanEvent(this, 'kanban-ticket-draft', { ticketId: this.ticket!.id, draft: this.draft });
  }

  private addComment = (input: Parameters<NonNullable<IKanbanProvider['addComment']>>[0]) =>
    this.provider.addComment!(input);

  public render() {
    const ticket = this.ticket;
    if (!ticket) return html``;
    const draft = this.draft ?? ticketDraft(ticket);
    const parent = this.tickets.find(item => item.id === ticket.parentId);
    const children = sortKanbanTickets(this.tickets.filter(item => item.parentId === ticket.id));
    const editable = Boolean(this.provider.updateTicket) && ticket.permissions?.edit !== false;
    const movable = Boolean(this.provider.moveTicket) && ticket.permissions?.move !== false;
    const parents = this.tickets.filter(
      item => item.kind === (ticket.kind === 'subtask' ? 'task' : 'epic'),
    );
    return html`<div class="toolbar row">
      ${this.canGoBack
        ? html`<dees-button
            size="sm"
            type="ghost"
            icon="lucide:ArrowLeft"
            text="Back"
            @clicked=${() => kanbanEvent(this, 'kanban-detail-back', {})}
          ></dees-button>`
        : ''}
      <span class="badge">${ticket.kind}</span>
      ${this.showTicketKey ? html`<span class="caption ticket-key">${ticket.key}</span>` : ''}
      ${parent
        ? html`<span class="caption" aria-hidden="true">/</span>
            <button
              class="link parent"
              title=${parent.title}
              @click=${() => kanbanEvent(this, 'kanban-ticket-open', { ticketId: parent.id })}
            >${parent.title}</button>`
        : ''}
      <span class="spacer"></span>
      ${editable && !this.editing
        ? html`<dees-button
            size="sm"
            type="ghost"
            text="Edit ticket"
            icon="lucide:Pencil"
            @clicked=${() => {
              this.editing = true;
              this.changeDraft({});
            }}
          ></dees-button>`
        : ''}
    </div>
    ${this.error ? html`<p class="error" role="alert">${this.error}</p>` : ''}
    <div class="layout">
      <section class="lead">
        ${this.editing && editable
          ? html`<div class="edit">
              <dees-input-text
                label="Title"
                .value=${draft.title}
                .disabled=${this.saving}
                @input=${(event: Event) =>
                  this.changeDraft({
                    title: String((event.currentTarget as DeesInputText).value ?? ''),
                  })}
              ></dees-input-text>
              <dees-input-textarea
                label="Description"
                .value=${draft.description}
                .rows=${5}
                .disabled=${this.saving}
                @input=${(event: Event) =>
                  this.changeDraft({ description: (event.currentTarget as DeesInputTextarea).value })}
              ></dees-input-textarea>
              <div class="row">
                <dees-button
                  size="sm"
                  type="accent"
                  text="Save changes"
                  .disabled=${this.saving || !draft.title.trim()}
                  .status=${this.saving ? 'pending' : 'normal'}
                  @clicked=${() =>
                    kanbanEvent(this, 'kanban-ticket-save', { ticketId: ticket.id, draft })}
                ></dees-button>
                <dees-button
                  size="sm"
                  type="ghost"
                  text="Cancel"
                  .disabled=${this.saving}
                  @clicked=${() => {
                    this.editing = false;
                    this.draft = undefined;
                    kanbanEvent(this, 'kanban-ticket-draft', {
                      ticketId: ticket.id,
                      draft: undefined,
                    });
                  }}
                ></dees-button>
              </div>
            </div>`
          : html`<h2>${ticket.title}</h2>
            <div
              class="description ${ticket.description ? '' : 'muted'}"
            >${ticket.description || 'No description yet.'}</div>`}
      </section>
      <aside>
        <div class="fields">
          <div>
            <label class="caption">Status</label>
            <dees-input-dropdown
              label="Status"
              .labelPosition=${'none'}
              .options=${this.columns.map(column => ({ key: column.id, option: column.title }))}
              .value=${{
                key: ticket.columnId,
                option:
                  this.columns.find(column => column.id === ticket.columnId)?.title ??
                  ticket.columnId,
              }}
              .disabled=${!movable || this.saving}
              @selectedOption=${(event: CustomEvent<{ key: string }>) =>
                kanbanEvent(this, 'kanban-ticket-move', {
                  ticketId: ticket.id,
                  columnId: event.detail.key,
                })}
            ></dees-input-dropdown>
          </div>
          ${this.editing && editable
            ? html`
              <div>
                <label class="caption">Priority</label>
                <dees-input-dropdown
                  label="Priority"
                  .labelPosition=${'none'}
                  .disabled=${this.saving}
                  .value=${{ key: draft.priority, option: draft.priority }}
                  .options=${['none', 'low', 'normal', 'high', 'urgent'].map(key => ({
                    key,
                    option: key,
                  }))}
                  @selectedOption=${(event: CustomEvent<{ key: TKanbanPriority }>) =>
                    this.changeDraft({ priority: event.detail.key })}
                ></dees-input-dropdown>
              </div>
              <div>
                <label class="caption">Assignee</label>
                <dees-input-dropdown
                  label="Assignee"
                  .labelPosition=${'none'}
                  .disabled=${this.saving}
                  .value=${{
                    key: draft.assigneeId,
                    option:
                      this.people.find(person => person.id === draft.assigneeId)?.name ??
                      ticket.assignee?.name ??
                      'Unassigned',
                  }}
                  .options=${[
                    { key: '', option: 'Unassigned' },
                    ...this.people.map(person => ({ key: person.id, option: person.name })),
                  ]}
                  @selectedOption=${(event: CustomEvent<{ key: string }>) =>
                    this.changeDraft({ assigneeId: event.detail.key })}
                ></dees-input-dropdown>
              </div>
              ${ticket.kind !== 'epic'
                ? html`<div>
                    <label class="caption">${ticket.kind === 'task' ? 'Epic' : 'Parent task'}</label>
                    <dees-input-dropdown
                      label=${ticket.kind === 'task' ? 'Epic' : 'Parent task'}
                      .labelPosition=${'none'}
                      .disabled=${this.saving}
                      .value=${{
                        key: draft.parentId,
                        option: parents.find(item => item.id === draft.parentId)?.title ?? 'None',
                      }}
                      .options=${[
                        ...(ticket.kind === 'task' ? [{ key: '', option: 'None' }] : []),
                        ...parents.map(item => ({ key: item.id, option: ticketLabel(item) })),
                      ]}
                      @selectedOption=${(event: CustomEvent<{ key: string }>) =>
                        this.changeDraft({ parentId: event.detail.key })}
                    ></dees-input-dropdown>
                  </div>`
                : ''}
            `
            : html`<div>
                <label class="caption">Priority</label>
                <span class="priority value" data-priority=${ticket.priority ?? 'none'}><dees-icon
                    .icon=${kanbanPriorityIcon(ticket.priority)}
                    iconSize="13"
                    aria-hidden="true"
                  ></dees-icon>${ticket.priority ?? 'none'}</span>
              </div>
              <div>
                <label class="caption">Assignee</label>
                <span class="person value"><span class="avatar" aria-hidden="true">${ticket.assignee
                  ? kanbanInitials(ticket.assignee.name)
                  : '–'}</span>${ticket.assignee?.name ?? 'Unassigned'}</span>
              </div>`}
          ${ticket.labels?.length
            ? html`<div>
                <label class="caption">Labels</label>
                <div class="row value">
                  ${ticket.labels.map(label => html`<span class="badge">${label}</span>`)}
                </div>
              </div>`
            : ''}
        </div>
      </aside>
      <div class="body">
        ${ticket.kind !== 'subtask'
          ? html`<section class="children">
              <div class="section-title">
                <h3>${ticket.kind === 'epic' ? 'Tasks' : 'Subtasks'}</h3>
                <span class="count">${children.length}</span>
                <span class="spacer"></span>
                ${this.provider.createTicket && ticket.permissions?.createChild !== false
                  ? html`<dees-button
                      size="sm"
                      type="ghost"
                      icon="lucide:Plus"
                      text=${ticket.kind === 'epic' ? 'Add task' : 'Add subtask'}
                      @clicked=${() =>
                        kanbanEvent(this, 'kanban-ticket-create', {
                          kind: ticket.kind === 'epic' ? 'task' : 'subtask',
                          parentId: ticket.id,
                          columnId: this.columns[0]?.id ?? ticket.columnId,
                        })}
                    ></dees-button>`
                  : ''}
              </div>
              <div class="child-list">${children.map(child => html`<button
                class="child"
                @click=${() => kanbanEvent(this, 'kanban-ticket-open', { ticketId: child.id })}
              >
                <span class="child-key">${child.key}</span>
                <span class="child-title">${child.title}</span>
                <span class="badge">${this.columns.find(
                  column => column.id === child.columnId,
                )?.title ?? child.columnId}</span>
              </button>`)}</div>
              ${!children.length
                ? html`<p class="caption">No ${ticket.kind === 'epic' ? 'tasks' : 'subtasks'} yet.</p>`
                : ''}
            </section>`
          : ''}
        <dees-kanban-discussion
          class="discussion"
          .ticketId=${ticket.id}
          .comments=${this.comments}
          .draft=${this.commentDraft}
          .loading=${this.loadingComments}
          .error=${this.commentsError}
          .readOnly=${ticket.permissions?.comment === false}
          .searchTickets=${this.searchTickets}
          .addComment=${this.provider.addComment ? this.addComment : undefined}
        ></dees-kanban-discussion>
      </div>
    </div>`;
  }

  public finishEditing(): void {
    this.editing = false;
  }
}
